
import {
  computed, defineComponent, reactive,
} from 'vue';
import Icone from '@/core/components/Icone.vue';
import Card from '@/core/components/Tela/Card.vue';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import { useTelaBase } from '@/core/composables/TelaBase';
import ServicoCipEstrutura from '@/servicos/Cadastros/PCP/ServicoCipEstrutura';
import { IEngenhariaCip } from '@/models/Entidades/Cadastros/PCP/IEngenhariaProduto';
import { ESazonalidade } from '@/core/models/Enumeradores/ESazonalidade';
import SelecionarCipEstrutura from './SelecionarCipEstrutura.vue';

export default defineComponent({
  name: 'EngenhariaCip',
  components: {
    Icone,
    Card,
    SelecionarCipEstrutura,
  },
  props: {
    cips: {
      type: Array as () => IEngenhariaCip[],
      required: true,
    },
    empresas: {
      type: Array as () => number[],
      required: true,
    },
    codigoEngenharia: {
      type: Number,
      required: true,
    },
    codigoTipoProducao: {
      type: Number,
      default: 0,
    },
    editavel: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:cips'],
  setup(props, { emit }) {
    const { apresentarMensagemAlerta } = useTelaBase();

    const servicoCipEstrutura = new ServicoCipEstrutura();
    servicoCipEstrutura.requisicaoSistema();

    const state = reactive({
      codigoCipEstrutura: 0,
      descricaoSelecionada: '',
      tipoPeriodoEstrutura: 0,
      cipSelecionada: -1,
    });

    const computedCips = computed({
      get: () => props.cips,
      set: (val: IEngenhariaCip[]) => {
        emit('update:cips', val);
      },
    });

    function obterDescricaoTipoPeriodo(tipoPeriodo: number): string {
      switch (tipoPeriodo) {
        case ESazonalidade.semanal:
          return 'Semanal';
        case ESazonalidade.quinzenal:
          return 'Quinzenal';
        case ESazonalidade.mensal:
          return 'Mensal';
        case ESazonalidade.bimestral:
          return 'Bimestral';
        case ESazonalidade.trimestral:
          return 'Trimestral';
        case ESazonalidade.semestral:
          return 'Semestral';
        case ESazonalidade.anual:
          return 'Anual';
        case ESazonalidade.customizada:
          return 'Customizado';
        default:
          return '';
      }
    }

    async function preencherEstruturaSelecionada() {
      if (!UtilitarioGeral.validaCodigo(state.codigoCipEstrutura)) {
        state.tipoPeriodoEstrutura = 0;
        return;
      }

      const estrutura = await servicoCipEstrutura.obterPorCodigo(state.codigoCipEstrutura);
      if (estrutura !== undefined && estrutura !== null) {
        state.descricaoSelecionada = estrutura.descricao;
        state.tipoPeriodoEstrutura = estrutura.tipoPeriodo;
      }
    }

    function limparCampos() {
      state.cipSelecionada = -1;
      state.codigoCipEstrutura = 0;
      state.descricaoSelecionada = '';
      state.tipoPeriodoEstrutura = 0;
    }

    function validarCampos(): boolean {
      if (!UtilitarioGeral.validaCodigo(state.codigoCipEstrutura)) {
        apresentarMensagemAlerta('Por favor selecione uma estrutura CIP!');
        return false;
      }

      if (state.cipSelecionada === -1 && computedCips.value.some((c) => c.codigoCipEstrutura === state.codigoCipEstrutura)) {
        apresentarMensagemAlerta('A estrutura CIP ja foi adicionada nesta engenharia!');
        return false;
      }

      if (state.cipSelecionada > -1) {
        const existeDuplicidade = computedCips.value.some((c, index) => index !== state.cipSelecionada && c.codigoCipEstrutura === state.codigoCipEstrutura);
        if (existeDuplicidade) {
          apresentarMensagemAlerta('A estrutura CIP ja foi adicionada nesta engenharia!');
          return false;
        }
      }

      return true;
    }

    function adicionar() {
      if (!validarCampos()) {
        return;
      }

      if (state.cipSelecionada !== -1) {
        computedCips.value[state.cipSelecionada].codigoCipEstrutura = state.codigoCipEstrutura;
        computedCips.value[state.cipSelecionada].descricaoCipEstrutura = state.descricaoSelecionada;
        computedCips.value[state.cipSelecionada].tipoPeriodoEstrutura = state.tipoPeriodoEstrutura;
      } else {
        const engenhariaCip = {} as IEngenhariaCip;
        engenhariaCip.codigo = 0;
        engenhariaCip.codigoEngenharia = props.codigoEngenharia;
        engenhariaCip.codigoCipEstrutura = state.codigoCipEstrutura;
        engenhariaCip.descricaoCipEstrutura = state.descricaoSelecionada;
        engenhariaCip.tipoPeriodoEstrutura = state.tipoPeriodoEstrutura;
        computedCips.value.push(engenhariaCip);
      }

      limparCampos();
    }

    function editar(index: number) {
      if (!props.editavel) {
        return;
      }

      const item = computedCips.value[index];
      state.cipSelecionada = index;
      state.codigoCipEstrutura = item.codigoCipEstrutura;
      state.descricaoSelecionada = item.descricaoCipEstrutura;
      state.tipoPeriodoEstrutura = item.tipoPeriodoEstrutura;
    }

    function excluir(index: number) {
      if (!props.editavel) {
        return;
      }

      computedCips.value.splice(index, 1);
      limparCampos();
    }

    return {
      props,
      state,
      UtilitarioGeral,
      computedCips,
      preencherEstruturaSelecionada,
      adicionar,
      editar,
      excluir,
      obterDescricaoTipoPeriodo,
    };
  },
});
